import React, { useState, useMemo, useEffect } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import CustomDateFilter from "../../../../CustomDatePicker/DateFilterSelect";
import DateFilterSelect from "../../../../CustomDatePicker/DateFilterSelectold";
import moment from "moment";
import Loader from '../../../../presentationals/Loader/Loader.component'
import {
  CalendarDays,
  BadgeCheck,
  Hourglass,
  Wallet,
  TrendingUp,
  IndianRupee,
  Funnel,
  ChevronLeft,
} from "lucide-react";
import ActionMenu from "./ActionMenu";

import {
  getNachOverviewScheduleByFilters,
  getNachScheduleUpcomingByLimit,
  getForecastVolumeByFilters,
  getCollectionVolumeByFilters,
  getForecastRiskAssessmentByFilters,
  getForecastInsightsByFilters,
  getScheduleNachListByFilters,
  downloadScheduleNachactiveApi
} from "../../MISNew/API's/Actioncreator";

<style>{`
       
        @keyframes shimmer {
         0% {
            background-position: 200% 0;
            }
        100% {
          background-position: -200% 0;
        }
        }
        
      `}</style>



const STATUS_MAP = [
  { label: "Scheduled", bg: "#dcfce7", color: "#16a34a" },
  { label: "Pending Approval", bg: "#fef3c7", color: "#d97706" },
  { label: "Cancelled", bg: "#fee2e2", color: "#dc2626" },
];

const MEDIUM_MAP = {
  CASHFREE: { bg: "#ede9fe", color: "#7c3aed" },
  RAZORPAY: { bg: "#dbeafe", color: "#2563eb" },
};

// ─── Shared styles ────────────────────────────────────────────
const card = {
  background: "#ffffff",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const TableRowSkeleton = ({ columns = 10 }) => {
  return (
    <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
      {Array.from({ length: columns }).map((_, idx) => (
        <td
          key={idx}
          style={{
            padding: "12px 16px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              height: 14,
              width: idx === 7 ? "80%" : "60%",
              margin: "0 auto",
              borderRadius: 6,
              background:
                "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.2s infinite",
            }}
          />
        </td>
      ))}
    </tr>
  );
};

const badge = (bg, color) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "5px 10px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 500,
  background: bg,
  color,
  whiteSpace: "nowrap",
});

// ─── Custom Tooltip for Area Chart ───────────────────────────
const VolumeTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          background: "white",
          borderRadius: 8,
          padding: "8px 12px",
          fontSize: 12,
          color: "#fff",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        }}
      >
        <p style={{ margin: 0, fontWeight: 600, marginBottom: 4, color: 'black' }}>{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ margin: 0, color: p.color }}>
            {p.name === "predicted" ? "Predicted Volume" : "Actual Volume"}:{" "}
            <strong>{p.value?.toLocaleString()}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CollectionTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          background: "white",
          borderRadius: 8,
          padding: "8px 12px",
          fontSize: 12,
          color: "#fff",
        }}
      >
        <p style={{ margin: 0, fontWeight: 600, marginBottom: 4, color: 'black' }}>{label}</p>
        {payload.map(
          (p, i) =>
            p.value && (
              <p key={i} style={{ margin: 0, color: p.color }}>
                {p.name === "expected" ? "Expected" : "Collected"}: ₹
                {p.value}
              </p>
            ),
        )}
      </div>
    );
  }
  return null;
};

// ─── Main Component ───────────────────────────────────────────
export default function ScheduleNach({
  commonFilter,
  handleBack,
  serviceProvider, setServiceProvider,
  handleFilter,
  gatewayList,
  retryFilter,
  setRetryFilter
}) {
  const [tableloader, setTableLoader] = useState(false);
  const [loader, setLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedDate, setSelectedDate] = useState("all"); // default "all" so no date filter blocks initial load
  const [currentPage, setCurrentPage] = useState(0);       // 0-based pagination (matches backend)
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const itemsPerPage = 10;
  const [isFilterCleared, setIsFilterCleared] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const [upcmingFilter, setUpcomingFilter] = useState(6);
  const [UPCOMING, setUPCOMING] = useState([]);
  const [VOLUME_FORECAST, setVOLUME_FORECAST] = useState([]);
  const [COLLECTION_FORECAST, setCOLLECTION_FORECAST] = useState([]);
  const [TABLE_DATA, setTABLE_DATA] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [RISK_DATA, setRISK_DATA] = useState([
    { label: "Low Risk", count: 0, pct: 0, color: "#22c55e" },
    { label: "Medium Risk", count: 0, pct: 0, color: "#f59e0b" },
    { label: "High Risk", count: 0, pct: 0, color: "#ef4444" },
  ]);
  const [KEY_INSIGHTS, setKEY_INSIGHTS] = useState([
    {
      id: 1,
      type: "positive",
      borderColor: "#22c55e",
      bgColor: "#f0fdf4",
      icon: <TrendingUp />,
      iconColor: "#22c55e",
      title: "Positive Trend",
      desc: "",
    },
    {
      id: 2,
      type: "alert",
      borderColor: "#7c3aed",
      bgColor: "#faf5ff",
      icon: <CalendarDays />,
      iconColor: "#7c3aed",
      title: "Peak Period",
      desc: "",
    },
    {
      id: 3,
      type: "info",
      borderColor: "#f59e0b",
      bgColor: "#fffbeb",
      icon: <IndianRupee />,
      iconColor: "#f59e0b",
      title: "Revenue Impact",
      desc: "",
    },
  ]);

  const [dateFilterPopup, setDateFilterPopup] = useState({
    show: false,
    from: moment().subtract(6, 'days').format("YYYY-MM-DD"),
    to: moment(new Date()).format("YYYY-MM-DD")
  });
  const [STATS, setSTATS] = useState([
    {
      id: 1,
      label: "Total Schedule",
      value: 0,
      icon: <CalendarDays size={20} color="#ffffff" />,
      iconBg: "linear-gradient(135deg, #0088FF 0%, #92CCFF 100%)",
      dark: false,
    },
    {
      id: 2,
      label: "Today's Schedule",
      value: 0,
      icon: <BadgeCheck size={20} color="#ffffff" />,
      iconBg: "linear-gradient(135deg, #327E45 0%, #9ED3AB 100%)",
      dark: false,
    },
    {
      id: 3,
      label: "Pending Approval",
      value: 0,
      icon: <Hourglass size={20} color="#ffffff" />,
      iconBg: "linear-gradient(135deg, #fde68a 0%, #d97706 100%)",
      dark: false,
    },
    {
      id: 4,
      label: "Total EMI Amount",
      value: 0,
      icon: <Wallet size={22} color="#ffffff" />,
      iconBg: "linear-gradient(135deg, #035865 0%, #25A3B6 100%)",
      dark: true,
    },
  ]);

  // ─── Overview API (stats + charts, excludes upcoming) ────────
  useEffect(() => {
    const obj = {
      from: commonFilter.fromDate,
      to: commonFilter.toDate,
      provider: serviceProvider,
    };
    overviewAPI(obj);
  }, [commonFilter.fromDate, commonFilter.toDate, serviceProvider]);

  // ─── Upcoming Schedule API: fires independently on limit change ─

  const handleClear = () => {
    const obj = {
      from: commonFilter.fromDate,
      to: commonFilter.toDate,
      provider: serviceProvider,
    };
    getNachScheduleUpcomingByLimit(
      upcmingFilter,
      (callback) => {
        setUPCOMING(callback?.data?.dateCards ?? []);
      }
    );
    overviewAPI(obj);
  }
  useEffect(() => {
    getNachScheduleUpcomingByLimit(
      upcmingFilter,
      (callback) => {
        setUPCOMING(callback?.data?.dateCards ?? []);
      }
    );
  }, [upcmingFilter]);

  // ─── Reset to page 0 when any filter changes ──────────────────
  useEffect(() => {
    setCurrentPage(0);
  }, [
    searchTerm,
    statusFilter,
    retryFilter,
    serviceProvider,
    selectedDate,
    commonFilter.fromDate,
    commonFilter.toDate,
  ]);

  // ─── Table API: fires on page or filter change ────────────────
  useEffect(() => {
    const payload = {
      from: commonFilter.fromDate,
      to: commonFilter.toDate,
      provider: serviceProvider,
      search: searchTerm,
      status: statusFilter,
      retry: retryFilter,
      date: selectedDate !== "all" ? selectedDate : "",
      page: currentPage,
    };
    tableAPI(payload);
  }, [
    commonFilter.fromDate,
    commonFilter.toDate,
    serviceProvider,
    searchTerm,
    statusFilter,
    retryFilter,
    selectedDate,
    currentPage,
  ]);

  const handleDateFilterPopup = (type, val) => {
    const formatted = moment(val).format("YYYY-MM-DD");
    if (type === "from" && commonFilter.fromDate !== formatted) {
      handleFilter("fromDate", formatted);
    } else if (type === "to" && commonFilter.toDate !== formatted) {
      handleFilter("toDate", formatted);
    }
  };


  const handleSelectedDateClick = (date) => {
    if (date === "all") {
      // restore original range — let parent hold the real range
      setSelectedDate("all");
      setCurrentPage(0);

    } else {
      const formatted = moment(date, ["DD-MM-YYYY", "YYYY-MM-DD", "MM/DD/YYYY"]).format("YYYY-MM-DD");
      setSelectedDate(date);
      setCurrentPage(0);
      // Set both from and to to the same date → triggers overviewAPI + tableAPI
      handleFilter("fromDate", formatted);
      handleFilter("toDate", formatted);
    }
  };

  // ─── Overview API ─────────────────────────────────────────────
  const overviewAPI = (data) => {
    setLoader(true);
    let pendingCalls = 5; // upcoming has its own useEffect, so only 5 calls here

    const handleDone = () => {
      pendingCalls -= 1;
      if (pendingCalls === 0) {
        setLoader(false);
      }
    };

    getNachOverviewScheduleByFilters(
      moment(data.from).format("DD-MM-YYYY"),
      moment(data.to).format("DD-MM-YYYY"),
      data.provider ?? "",
      (callback) => {
        const d = callback?.data ?? {};
        setSTATS([
          {
            id: 1,
            label: "Total Schedule",
            value: d?.totalSchedule ?? 0,
            icon: <CalendarDays size={20} color="#ffffff" />,
            iconBg: "linear-gradient(135deg, #0088FF 0%, #92CCFF 100%)",
            dark: false,
          },
          {
            id: 2,
            label: "Today's Schedule",
            value: d?.todaySchedule ?? 0,
            icon: <BadgeCheck size={20} color="#ffffff" />,
            iconBg: "linear-gradient(135deg, #327E45 0%, #9ED3AB 100%)",
            dark: false,
          },
          {
            id: 3,
            label: "Pending Approval",
            value: d?.pendingApproval ?? 0,
            icon: <Hourglass size={20} color="#ffffff" />,
            iconBg: "linear-gradient(135deg, #fde68a 0%, #d97706 100%)",
            dark: false,
          },
          {
            id: 4,
            label: "Total EMI Amount",
            value: d?.totalEmiAmount?.toLocaleString() ?? 0,
            icon: <Wallet size={22} color="#ffffff" />,
            iconBg: "linear-gradient(135deg, #035865 0%, #25A3B6 100%)",
            dark: true,
          },
        ]);
        handleDone();
      }
    );

    getForecastVolumeByFilters(
      moment(data.from).format("DD-MM-YYYY"),
      moment(data.to).format("DD-MM-YYYY"),
      data.provider ?? "",
      (callback) => {
        setVOLUME_FORECAST(callback?.data?.dataPoints ?? []);
        handleDone();
      }
    );

    getCollectionVolumeByFilters(
      moment(data.from).format("DD-MM-YYYY"),
      moment(data.to).format("DD-MM-YYYY"),
      data.provider ?? "",
      (callback) => {
        setCOLLECTION_FORECAST(callback?.data?.dataPoints ?? []);
        handleDone();
      }
    );

    getForecastRiskAssessmentByFilters(
      moment(data.from).format("DD-MM-YYYY"),
      moment(data.to).format("DD-MM-YYYY"),
      data.provider ?? "",
      (callback) => {
        setRISK_DATA([
          { label: "Low Risk", count: callback?.data?.lowRiskCount ?? 0, pct: callback?.data?.lowRiskPct ?? 0, color: "#22c55e" },
          { label: "Medium Risk", count: callback?.data?.mediumRiskCount ?? 0, pct: callback?.data?.mediumRiskPct ?? 0, color: "#f59e0b" },
          { label: "High Risk", count: callback?.data?.highRiskCount ?? 0, pct: callback?.data?.highRiskPct ?? 0, color: "#ef4444" },
        ]);
        handleDone();
      }
    );

    getForecastInsightsByFilters(
      moment(data.from).format("DD-MM-YYYY"),
      moment(data.to).format("DD-MM-YYYY"),
      data.provider ?? "",
      (callback) => {

        const insights = callback?.data?.insights;
        if (insights && insights.length > 0) {
const configMap = {
        POSITIVE_TREND: {
          id: 1,
          type: "positive",
          borderColor: "#22c55e",
          bgColor: "#f0fdf4",
          icon: <TrendingUp />,
          iconColor: "#22c55e",
        },
        PEAK_PERIOD: {
          id: 2,
          type: "alert",
          borderColor: "#7c3aed",
          bgColor: "#faf5ff",
          icon: <CalendarDays />,
          iconColor: "#7c3aed",
        },
        REVENUE_IMPACT: {
          id: 3,
          type: "info",
          borderColor: "#f59e0b",
          bgColor: "#fffbeb",
          icon: <IndianRupee />,
          iconColor: "#f59e0b",
        }
      };

      const formattedInsights = insights
        .filter(item => item?.description && configMap[item.type]) // only valid + supported types
        .map(item => ({
          ...configMap[item.type],
          title: item.title, // use API title
          desc: item.description,
        }));

          setKEY_INSIGHTS(formattedInsights);
        }else{
          setKEY_INSIGHTS([]);
        }
        // else keep the default state as-is
        handleDone();
      }
    );
  };

  // ─── Table API ────────────────────────────────────────────────
  const tableAPI = (data) => {
    setTableLoader(true);
    getScheduleNachListByFilters(
      data.search || "",
      moment(data.from).format("DD-MM-YYYY"),
      moment(data.to).format("DD-MM-YYYY"),
      data.retry || "",
      data.provider || "",
      data.status || "",
      // data.date || "",    
      data.page,          // 1-based page number
      itemsPerPage,
      (callback) => {
        setTableLoader(false);
        setTABLE_DATA(callback?.data?.records || []);
        setTotalPages(callback?.data?.totalPages || 1);
        setTotalRecords(callback?.data?.totalElements ?? 0);
      }
    );
  };



  const selectStyle = {
    padding: "8px 32px 8px 12px",
    fontSize: 13,
    color: "#374151",
    background: `#f9fafb`,
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    cursor: "pointer",
    outline: "none",
    appearance: "none",
    minWidth: 150,
    fontFamily: "inherit",
  };

  const handleDownload = () => {
    setLoader(true)
    downloadScheduleNachactiveApi(moment(commonFilter.fromDate).format("DD-MM-YYYY"),
      moment(commonFilter.toDate).format("DD-MM-YYYY"), statusFilter, serviceProvider, callback => {
        setLoader(false)
      })
  }


  return (
    <>
      {loader ? <Loader /> : ""}
      <div
        style={{
          fontFamily: "'Inter','Segoe UI',sans-serif",
          background: "#f9fafb",
          minHeight: "100vh",
          padding: "24px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <h1
            style={{
              fontSize: 20,
              fontWeight: 500,
              color: "#111827",
              margin: 0,
              letterSpacing: "-0.02em",
              display: 'inline-flex',
              gap: '5px',
              cursor: 'pointer'
            }}
            onClick={(e) => handleBack(e)}
          >
            <ChevronLeft style={{ width: 'auto' }} /> Schedule NACH
          </h1>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select
              value={serviceProvider}
              onChange={(e) => setServiceProvider(e.target.value)}
              style={selectStyle}
            >
              <option value="">Service Provider's</option>
              {gatewayList?.length > 0 && gatewayList?.map((val) => (
                <option key={val} value={val}>{val?.charAt(0) + val?.slice(1)?.toLowerCase()}</option>
              ))}
            </select>
            <button
              onClick={() => handleDownload()}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 18px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                border: "none",
                background: "#035865",
                color: "#fff",
              }}
            >
              ⬇ Export Schedule
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {STATS && STATS?.length && STATS?.map((s) =>
            s.dark ? (
              <div
                key={s?.id}
                style={{
                  ...card,
                  background: s.iconBg,
                  border: "none",
                  padding: 20,
                  flex: "1 1 160px",
                  minWidth: 160,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: "white" }}>{s.label}</span>
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: "rgba(255,255,255,0.15)",
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                    }}
                  >
                    {s?.icon}
                  </div>
                </div>
                <div style={{ fontSize: 26, fontWeight: 700, color: "#fff", letterSpacing: "-0.02em" }}>
                  {s?.value}
                </div>
              </div>
            ) : (
              <div
                key={s?.id}
                style={{
                  ...card, padding: 20,
                  flex: "1 1 140px", minWidth: 140,
                  display: "flex", flexDirection: "column",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#6b7280" }}>{s?.label}</span>
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: s.iconBg,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                    }}
                  >
                    {s?.icon}
                  </div>
                </div>
                <div style={{ fontSize: 26, fontWeight: 500, color: "#111827", letterSpacing: "-0.02em" }}>
                  {s?.value}
                </div>
              </div>
            ),
          )}
        </div>

        <div
          style={{
            ...card,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 12, padding: "12px 16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", flex: 1 }}>
            <div
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "8px 12px", background: "#f9fafb",
                border: "1px solid #e5e7eb", borderRadius: 8, minWidth: 180,
              }}
            >
              <span style={{ color: "#9ca3af", fontSize: 14 }}>🔍</span>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  border: "none", outline: "none", background: "transparent",
                  fontSize: 13, color: "#374151", width: "100%", fontFamily: "inherit",
                }}
              />
            </div>

            <DateFilterSelect
              customStyle={{ padding: '8px 12px', border: "1px solid rgb(229, 231, 235)", background: "rgb(249, 250, 251)" }}
              value={[new Date(commonFilter.fromDate), new Date(commonFilter.toDate)]}
              onChange={(range) => {
                handleDateFilterPopup("from", range[0]);
                handleDateFilterPopup("to", range[1]);
              }}
              isFilterCleared={isFilterCleared}
            />

            {/* <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={selectStyle}
            >
              <option value="">Status</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Pending Approval">Pending Approval</option>
              <option value="Cancelled">Cancelled</option>
            </select> */}

            {/* <select
              value={retryFilter}
              onChange={(e) => setRetryFilter(e.target.value)}
              style={selectStyle}
            >
              <option value="">Retry Count</option>
              <option value="1">1</option>
              <option value="3">3</option>
              <option value="6">6</option>
            </select> */}
          </div>

          <div style={{ display: "flex", gap: 8 }}>

            <button
              onClick={() => {
                setSearchTerm("");
                setDateRange("");
                setStatusFilter("");
                setRetryFilter("");
                setSelectedDate("all");
                setCurrentPage(0);
                setIsFilterCleared(true);
                setTimeout(() => setIsFilterCleared(false), 300);
                handleClear();
              }}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 36, height: 36, borderRadius: 8,
                background: "#f3f4f6", color: "#6b7280",
                border: "1px solid #e5e7eb", cursor: "pointer", fontSize: 15,
              }}
            >
              ✕
            </button>
          </div>
        </div>

        <div style={{ ...card, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: "flex-start", marginBottom: '16px' }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: "#111827", margin: "0" }}>
              Upcoming Schedule
            </h3>
            <select
              value={upcmingFilter}
              onChange={(e) => setUpcomingFilter(e.target.value)}
              style={{ ...selectStyle, minWidth: '80px' }}
            >
              <option value="3">3</option>
              <option value="6">6</option>
              <option value="9">9</option>
              <option value="12">12</option>
              <option value="15">15</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 12, overflowX: 'auto', scrollbarWidth: "thin" }}>


            {UPCOMING && UPCOMING?.length > 0 && UPCOMING?.map((u) => {
              const isActive = selectedDate === u?.date;
              return (
                <div
                  key={u.date}
                  onClick={() => handleSelectedDateClick(u?.date)}
                  style={{
                    flexShrink: "0",
                    //  minWidth: "170px",
                    width: `calc(100%/ 5 )`,
                    padding: "14px 16px", borderRadius: 10, cursor: "pointer",
                    border: isActive ? "2px solid #0d9488" : "1px solid #e5e7eb",
                    background: isActive ? "#f0fdfa" : "#fafafa",
                    display: "flex", flexDirection: "column", gap: 10,
                    transition: "all 0.15s",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div
                      style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: isActive ? "#ede9fe" : "#f3f0ff",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}
                    >
                      <CalendarDays size={16} color={isActive ? "#7c3aed" : "#a78bfa"} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? "#0d9488" : "#374151" }}>
                      {u?.date}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <span style={{ fontSize: 11, color: "#9ca3af" }}>Total Schedule</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>{u?.count ?? 0}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>



        <div style={{ ...card, overflow: "hidden" }}>
          <div
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "14px 20px", borderBottom: "1px solid #f3f4f6",
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>Schedule Records</span>
            {/* FIX: show server totalRecords, not client filtered.length */}
            <span style={{ fontSize: 13, color: "#6b7280" }}>Total: {totalRecords} records</span>
          </div>

          <div style={{ overflowX: "auto" }}>
            {/* FIX: show loader overlay on table during fetch */}

            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  {[
                    "Loan ID", "Customer Name", "Date", "Bank Name",
                    "NACH Status", "Retry Count", "Medium", "Status",
                    "Failure Reason", "Last Attempt", 
                    // "Actions",
                  ]?.map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 14px", textAlign: "center",
                        fontSize: 11, fontWeight: 600, color: "#6b7280",
                        textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableloader ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRowSkeleton key={i} columns={10} />
                  ))
                )
                  :
                  TABLE_DATA?.length == 0 ?
                    <tr>
                      <td colSpan={11} style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
                        No records found
                      </td>
                    </tr>
                    : (
                      TABLE_DATA && TABLE_DATA?.length > 0 && TABLE_DATA?.map((row) => (
                        <tr
                          key={row.id}
                          style={{ borderBottom: "1px solid #f3f4f6", background: "#fff", transition: "background 0.15s" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f0fdfa")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                        >
                          <td style={{ padding: "11px 14px", fontSize: 13, color: "#374151", fontWeight: 500, textAlign: "center" }}>
                            {row.loanId}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: 13, color: "#374151", textAlign: "center" }}>
                            {row.customerName}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: 13, fontWeight: 600, textAlign: "center",minWidth:"100px" }}>
                            {row.scheduledDate}
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "center" }}>
                            <span style={{ ...badge("#0088FF", "white"), borderRadius: 6, fontWeight: 500 }}>
                              {row.bankName}
                            </span>
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "center" }}>
                            {row?.nachStatus === "success" ? (
                              <span
                                style={{
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                  width: 18, height: 18, borderRadius: 4,
                                  border: "1.5px solid #16a34a", color: "#16a34a", fontSize: 13,
                                }}
                              >✓</span>
                            ) : (
                              <span
                                style={{
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                  width: 18, height: 18, borderRadius: 4,
                                  border: "1.5px solid #dc2626", color: "#dc2626", fontSize: 13,
                                }}
                              >✗</span>
                            )}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: 13, color: "#374151", textAlign: "center" }}>
                            {row.retryCount}
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "center" }}>
                            <span style={badge(MEDIUM_MAP[row.medium]?.bg ?? "#f3f4f6", MEDIUM_MAP[row.medium]?.color ?? "#374151")}>
                              {row.medium}
                            </span>
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "center" }}>
                            <span style={badge(row.statusBg, row.statusColor)}>{row.status}</span>
                          </td>
                          <td style={{ padding: "11px 14px", display: "flex", justifyContent: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: row.failureColor, flexShrink: 0 }} />
                              <span style={{ fontSize: 13, color: "#374151", textAlign: "center" }}>{row.failureReason ?? "-"}</span>
                            </div>
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: 13, color: "#6b7280", textAlign: "center" }}>
                            {row.lastAttempt ?? "-"}
                          </td>
                          {/* <td style={{ padding: "11px 14px", textAlign: "center" }}>
                            <button
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "black", fontWeight: 800 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setAnchorEl(e.currentTarget);
                                setSelectedRow(row);
                              }}
                            >
                              ⋮
                            </button>
                          </td> */}
                        </tr>
                      ))
                    )
                }


              </tbody>
            </table>

          </div>

          {/* Pagination — server-driven */}
          <div
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 6, padding: 14, borderTop: "1px solid #f3f4f6", flexWrap: "wrap",
            }}
          >
            <button
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              style={{
                padding: "6px 12px", fontSize: 13, borderRadius: 8,
                border: "1px solid #e5e7eb", background: "#fff", color: "#374151",
                cursor: currentPage === 0 ? "not-allowed" : "pointer",
                opacity: currentPage === 0 ? 0.4 : 1,
              }}
            >
              ← Previous
            </button>

            {/* 0-based page buttons: generate 0..totalPages-1, display as 1..totalPages */}
            {Array.from({ length: totalPages }, (_, i) => i)
              .filter((p) => p === 0 || p === totalPages - 1 || Math.abs(p - currentPage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "..." ? (
                  <span key={`ellipsis-${idx}`} style={{ padding: "0 4px", color: "#9ca3af" }}>…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    style={{
                      width: 34, height: 34, borderRadius: 8, border: "1px solid",
                      fontSize: 13, cursor: "pointer",
                      fontWeight: p === currentPage ? 600 : 400,
                      background: p === currentPage ? "#035865" : "#fff",
                      borderColor: p === currentPage ? "#035865" : "#e5e7eb",
                      color: p === currentPage ? "#fff" : "#374151",
                    }}
                  >
                    {p + 1}  {/* display 1-based to user */}
                  </button>
                )
              )}

            <button
              disabled={currentPage === totalPages - 1}
              onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
              style={{
                padding: "6px 12px", fontSize: 13, borderRadius: 8,
                border: "1px solid #e5e7eb", background: "#fff", color: "#374151",
                cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                opacity: currentPage === totalPages - 1 ? 0.4 : 1,
              }}
            >
              Next →
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={{ ...card, padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: 0 }}>NACH Volume Forecast</h3>
              <div style={{ display: "flex", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#7c3aed" }} /> Predicted Volume
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#10b981" }} /> Actual Volume
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={VOLUME_FORECAST} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="predictedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis dataKey="monthLabel" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <Tooltip content={<VolumeTooltip />} />
                <Area type="monotone" dataKey="predictedVolume" stroke="#7c3aed" strokeWidth={2} fill="url(#predictedGrad)" dot={false} activeDot={{ r: 6, fill: "#7c3aed", strokeWidth: 0 }} connectNulls />
                <Area type="monotone" dataKey="actualVolume" stroke="#10b981" strokeWidth={2} fill="url(#actualGrad)" dot={{ r: 5, fill: "#10b981", strokeWidth: 0 }} activeDot={{ r: 6 }} connectNulls />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div style={{ ...card, padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: 0 }}>Collection Forecast</h3>
              <div style={{ display: "flex", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#7c3aed" }} /> Expected
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#10b981" }} /> Collected
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={COLLECTION_FORECAST} barSize={32} barGap={6} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis dataKey="monthLabel" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <Tooltip content={<CollectionTooltip />} />
                <Bar dataKey="collectedLakh" fill="#4FA564" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expectedLakh" fill="#7C3AED" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={{ ...card, padding: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: "0 0 20px" }}>Risk Assessment</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              {RISK_DATA && RISK_DATA?.length > 0 && RISK_DATA?.map((r) => (
                <div key={r.label}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: r.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>{r?.label}</span>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{r?.count?.toLocaleString()}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ flex: 1, height: 8, borderRadius: 999, background: "#f3f4f6", overflow: "hidden" }}>
                      <div style={{ width: `${r?.pct}%`, height: "100%", borderRadius: 999, background: r?.color, transition: "width 0.6s ease" }} />
                    </div>
                    <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 10, minWidth: 32, textAlign: "right" }}>{r?.pct}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...card, padding: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: "0 0 16px" }}>Key Insights</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {KEY_INSIGHTS && KEY_INSIGHTS?.length > 0 && KEY_INSIGHTS?.map((ins) => (
                <div
                  key={ins.id}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 14,
                    padding: "14px 16px", borderRadius: 10,
                    background: ins?.bgColor, borderLeft: `4px solid ${ins?.borderColor}`,
                  }}
                >
                  <div
                    style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: "#fff", border: `1px solid ${ins.borderColor}30`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 14, color: ins.iconColor, fontWeight: 700,
                    }}
                  >
                    {ins?.icon}
                  </div>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: "#111827", margin: "0 0 3px" }}>{ins?.title}</p>
                    <p style={{ fontSize: 12, color: "#6b7280", margin: 0, lineHeight: 1.5 }}>{ins?.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <ActionMenu
          rowData={selectedRow}
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={() => setAnchorEl(null)}
        />
      </div>
    </>
  );
}